import axios from "axios";
import API from "../axios";

/**
 * Uploads a file straight to S3 using a backend-issued presigned POST.
 *
 * The binary never travels through the backend — only a small, authenticated
 * presign request does. The backend builds a company-scoped key and pre-creates
 * the File record, so the returned fileId is already persisted and the broadcast
 * flow can resolve it by url.
 *
 * @param {File} file - File to upload.
 * @param {{ folder?: string, onProgress?: (progress: number) => void }} [options]
 * @returns {Promise<{ url: string, fileId: string }>} Public url and File id.
 */
export const uploadFileViaPresignedPost = async (
  file,
  { folder = "uploads", onProgress = () => {} } = {}
) => {
  if (!file) {
    throw new Error("No se seleccionó un archivo");
  }

  // 1. Ask the backend for a presigned POST (authenticated, company-scoped key).
  const { data } = await API.post("/uploadFile/presign", {
    name: file.name,
    contentType: file.type,
    weight: file.size,
    folder,
  });

  const { upload, publicUrl, fileId } = data;

  // 2. Upload the binary directly to S3. Presigned-POST fields must precede the
  //    file part, and we use a bare axios instance so our auth token is never
  //    sent to S3.
  const formData = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);

  await axios.post(upload.url, formData, {
    onUploadProgress: (event) => {
      if (event.total) {
        onProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  });

  return { url: publicUrl, fileId };
};

/**
 * Uploads a file to an album via presigned POST using a two-step (presign +
 * confirm) flow. The backend File record is created only on confirm — after S3
 * accepts the upload — so a failed upload never leaves an orphan row or a broken
 * album thumbnail.
 *
 * @param {File} file - File to upload.
 * @param {{ albumId: string, onProgress?: (progress: number) => void }} options
 * @returns {Promise<object>} The persisted File record (linked to the album).
 */
export const uploadAlbumFileViaPresignedPost = async (
  file,
  { albumId, onProgress = () => {} } = {}
) => {
  if (!file) {
    throw new Error("No se seleccionó un archivo");
  }

  // 1. Presign WITHOUT pre-creating the File (created on confirm to avoid orphans).
  const { data: presign } = await API.post("/uploadFile/presign", {
    name: file.name,
    contentType: file.type,
    weight: file.size,
    folder: "albums",
    createRecord: false,
  });

  const { upload, publicUrl, fileType } = presign;

  // 2. Upload the binary straight to S3.
  const formData = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);

  await axios.post(upload.url, formData, {
    onUploadProgress: (event) => {
      if (event.total) {
        onProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  });

  // 3. Confirm: persist the File and link it to the album now that S3 accepted it.
  const { data: createdFile } = await API.post("/uploadFile/confirm", {
    name: file.name,
    url: publicUrl,
    fileType,
    weight: file.size,
    albumId,
  });

  return createdFile;
};
